import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Button from 'components/Button';
import { makeStyles } from 'tss-react/mui';
import { CheckCircleOutline } from '@mui/icons-material';
import PageContent from 'components/PageContent';

const useStyles = makeStyles()((theme) => ({
  tickIcon: {
    color: theme.palette.text.rootColor,
    opacity: 0.4,
    width: '140px !important',
    height: '140px !important',
  },
  title: {
    fontSize: 26,
    color: theme.palette.text.rootColor,
    marginBottom: '1rem',
  },
  additionalText: {
    color: '#656565',
    marginBottom: '23px',
    fontSize: 15,
    maxWidth: 590,
  },
  textCenter: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  flexBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  primaryButton: {
    marginBottom: '15px',
  },
  secondaryButton: {
    marginBottom: '15px',
  },
  tertiaryButton: {
    marginBottom: '15px',
  },
  bottomText: {
    color: '#656565',
    fontSize: 14,
    maxWidth: 600,
    marginTop: '0.4rem',
  },
  buttonWidth: {
    minWidth: 280,
  },
  link: {
    color: theme.palette.text.rootColor,
  },
}));

type Props = {
  title: string;
  additionalText: ReactNode;
  proceedText: string;
  proceedLink?: string;
  proceedCallback?: () => void;
  secondaryText?: string;
  secondaryLink?: string;
  secondaryLoading?: boolean;
  secondaryLoadingText?: string;
  secondaryCallback?: () => void;
  tertiaryText?: string;
  tertiaryLink?: string;
  tertiaryCallback?: () => void;
  bottomText?: ReactNode;
  isFullPage?: boolean;
  children?: any;
};

const FullPageSuccess: FC<Props> = ({
  title,
  additionalText,
  proceedText,
  proceedLink,
  proceedCallback = null,
  secondaryText = null,
  secondaryLink = null,
  secondaryLoading = false,
  secondaryLoadingText = null,
  secondaryCallback = null,
  tertiaryText = null,
  tertiaryLink = null,
  tertiaryCallback = null,
  bottomText = null,
  children = null,
  isFullPage = true,
}) => {
  const { classes } = useStyles();

  const ProceedButton = ({
    proceedCallback,
  }: {
    proceedCallback?: () => void;
  }) => (
    <Button
      className={`${classes.primaryButton} ${classes.buttonWidth}`}
      color="primary"
      variant="contained"
      onClick={proceedCallback}
    >
      {proceedText}
    </Button>
  );

  const SecondaryButton = ({
    secondaryCallback,
  }: {
    secondaryCallback?: () => void;
  }) => (
    <Button
      className={`${classes.secondaryButton} ${classes.buttonWidth}`}
      color="primary"
      variant="outlined"
      loading={secondaryLoading}
      onClick={secondaryCallback}
    >
      {secondaryLoading && !!secondaryLoadingText
        ? secondaryLoadingText
        : secondaryText}
    </Button>
  );

  const TertiaryButton = ({
    tertiaryCallback,
  }: {
    tertiaryCallback?: () => void;
  }) => (
    <Button
      className={`${classes.tertiaryButton} ${classes.buttonWidth}`}
      color="primary"
      variant="outlined"
      onClick={tertiaryCallback}
    >
      {tertiaryText}
    </Button>
  );

  const hasSecondaryLink = secondaryText && secondaryLink;
  const hasSecondaryCallback = secondaryText && secondaryCallback;
  const hasTertiaryLink = tertiaryText && tertiaryLink;
  const hasTertiaryCallback = tertiaryText && tertiaryCallback;

  return (
    <PageContent fullHeight={isFullPage}>
      <div className={classes.flexBox}>
        <div className={classes.textCenter}>
          <CheckCircleOutline className={classes.tickIcon} />
          <h3 className={classes.title}>{title}</h3>
          <p className={classes.additionalText}>{additionalText}</p>
          {proceedCallback ? (
            <ProceedButton proceedCallback={proceedCallback} />
          ) : (
            <Link to={proceedLink ?? '#'}>
              <ProceedButton />
            </Link>
          )}

          {hasSecondaryCallback ? (
            <SecondaryButton secondaryCallback={secondaryCallback} />
          ) : hasSecondaryLink ? (
            <Link to={secondaryLink}>
              <SecondaryButton />
            </Link>
          ) : null}

          {hasTertiaryCallback ? (
            <TertiaryButton tertiaryCallback={tertiaryCallback} />
          ) : hasTertiaryLink ? (
            <Link to={tertiaryLink}>
              <TertiaryButton />
            </Link>
          ) : null}

          {bottomText && <p className={classes.bottomText}>{bottomText}</p>}

          <div className={classes.additionalText}>{children}</div>
        </div>
      </div>
    </PageContent>
  );
};
export default FullPageSuccess;
